import { Component, Renderer2, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ModalController, Platform } from '@ionic/angular';
import { Observable, of } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { Network } from '@capacitor/network';

import * as rootReducer from 'app/core/store';
import { PlatformService } from 'app/core/services/platform.service';
import { selectDownloadedCount } from 'app/core/store/selectors/offline.selectors';
import { ConfigModalAnimation } from 'app/animations/config-modal-animation';
import * as fromSelectors from './store/header.selectors';
import * as fromActions from './store/header.actions';
import { HeaderConstants } from '../header.constants';
import { ModalsService } from '../../modals/modals.service';
import { PreferencesComponent } from '../preferences/preferences.component';

@Component({
  standalone: false,
  selector: 'pptl-header-main-view',
  templateUrl: './header-main-view.component.html',
  styleUrls: ['header-main-view.component.scss', '../header.scss'],
})
export class HeaderMainViewComponent implements OnInit {
  title = '';
  buttons = HeaderConstants.buttons;
  favoriteCount$: Observable<number>;
  downloadedCount$: Observable<number>;
  inboxCount$: Observable<number>;
  isHomePage = false;

  private settingModal;

  constructor(
    public router: Router,
    private store: Store<rootReducer.State>,
    public modalControllerPreferences: ModalController,
    private platformService: PlatformService,
    private renderer: Renderer2,
    private platform: Platform,
    private modalsService: ModalsService,
    private animation: ConfigModalAnimation,
  ) {
    this.store.dispatch(fromActions.loadCount());

    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.setHeaderTitle();
      }
    });

    this.downloadedCount$ = this.store.select(selectDownloadedCount);
    this.favoriteCount$ = this.store.pipe(select(fromSelectors.getFavoriteCount));
    this.inboxCount$ = this.store.pipe(select(fromSelectors.getInboxCount));

    this.hideOfflineButton();
    this.ifSearch();
  }

  ngOnInit() {
    this.platform.ready().then(() => {
      Network.addListener('networkStatusChange', ({ connected }) => {
        if (!connected && this.settingModal) {
          this.settingModal.dismiss();
        }
      });
    });
  }

  hideOfflineButton(): void {
    if (!this.platformService.isCordova()) {
      this.buttons = this.buttons.filter((button: any) => button.id !== 'offline');
    }
  }

  getButtonBadgeCount(button: any): Observable<number> {
    const buttonHash = {
      favorite: this.favoriteCount$,
      offline: this.downloadedCount$,
      inbox: this.inboxCount$,
    };

    if (buttonHash[button.id]) {
      return buttonHash[button.id];
    }

    return of(0);
  }

  isBudgeShowed(buttonId: string): boolean {
    return ['offline', 'inbox', 'favorite'].includes(buttonId);
  }

  onMenuButtonClick(buttonId: string): void {
    if (buttonId !== 'user') {
      this.router.navigate([`/${buttonId}`]);
      buttonId === 'search'
        ? this.renderer.addClass(document.body, 'search')
        : this.renderer.removeClass(document.body, 'search');
    } else {
      this.isHomePage = this.router.url.includes('home');
      this.showModal();
    }
  }

  async showModal() {
    const disabledFilter = ['/settings'];

    if (!disabledFilter.includes(this.router.url)) {
      this.modalsService.showModal({
        enterAnimation: this.animation.getAnimations().enterAnimation,
        leaveAnimation: this.animation.getAnimations().leaveAnimation,
        cssClass: 'modal-preferences',
        component: PreferencesComponent,
        componentProps: { isHomePage: false },
      });
    }
  }

  private setHeaderTitle(): void {
    const title: string[] = this.router.url.split('/');
    const titleHash = {
      current: 'Inbox',
      history: 'Inbox',
      favorite: 'Favorite',
      search: 'Search',
      offline: 'Offline',
      'help-page': 'Help',
      'application-settings': 'Application Settings',
      'cache-size-settings': 'Application Settings',
      'account-settings': 'Account Settings',
      'edit-name': 'Account Settings',
      'edit-password': 'Account Settings',
      'edit-company': 'Account Settings',
    };

    const key = title[title.length - 1];
    this.title = titleHash[key];
  }

  isActivePage(page: string): boolean {
    if (page === 'inbox' && this.router.url.includes(page)) {
      return !this.router.url.includes('favorite');
    }

    return this.router.url.includes(page);
  }

  getButtons() {
    this.buttons = HeaderConstants.buttons;
    this.hideOfflineButton();
    return this.buttons;
  }

  get isDevicePlatform(): boolean {
    return this.platformService.isCordova();
  }

  private ifSearch(): void {
    this.router.url.includes('search')
      ? this.renderer.addClass(document.body, 'search')
      : this.renderer.removeClass(document.body, 'search');
  }
}
